'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/routing'
import { N_FREE_DASHBOARD_BOOKINGS_MONTHLY, N_FREE_ITEMS, N_FREE_SECTIONS, getAnnualPerMonth } from '@/config'
import type { Currency } from '@/lib/interfaces/interfaces'
import type { PaidPlan } from '@/lib/utils/subscriptionPlan'
import '../../sass/landing/PricingV2.scss'

const cardMotion = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, delay }
})

// Precio por mes con el plan anual (el más bajo), igual que el resto de la web.
const formatPlanPrice = (plan: PaidPlan, currency: Currency) => {
  const amount = getAnnualPerMonth(plan, currency)
  return currency === '€' ? `${amount}€` : `${currency}${amount}`
}

const PricingV2 = () => {
  const t = useTranslations('LandingV2.pricing')
  const [currency, setCurrency] = useState<Currency>('€')

  useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (timezone.toLowerCase().includes('america')) setCurrency('$')
    } catch {
      /* noop */
    }
  }, [])

  const freePerks = [
    t('free.perks.p1', { N_FREE_ITEMS }),
    t('free.perks.p2', { N_FREE_SECTIONS }),
    t('free.perks.p3'),
    t('free.perks.p4'),
    t('free.perks.p5'),
    t('free.perks.p6'),
    t('free.perks.p7'),
    t('free.perks.p8')
  ]
  const menuPerks = [
    t('menuPlan.perks.p1'),
    t('menuPlan.perks.p2'),
    t('menuPlan.perks.p3'),
    t('menuPlan.perks.p4'),
    t('menuPlan.perks.p5'),
    t('menuPlan.perks.p6'),
    t('menuPlan.perks.p7', { count: N_FREE_DASHBOARD_BOOKINGS_MONTHLY })
  ]
  const fullPerks = [
    t('fullPlan.perks.p1'),
    t('fullPlan.perks.p2'),
    t('fullPlan.perks.p3'),
    t('fullPlan.perks.p4'),
    t('fullPlan.perks.p5'),
    t('fullPlan.perks.p6')
  ]

  const perkList = (perks: string[]) => (
    <ul className='pricing__list'>
      {perks.map(perk => (
        <li key={perk} className='pricing__list-item'>
          <Check size={16} /> {perk}
        </li>
      ))}
    </ul>
  )

  return (
    <section id='pricing' className='pricing'>
      <div className='pricing__container'>
        <div className='pricing__header'>
          <span className='landing-v2__chip'>{t('chip')}</span>
          <h2>{t('heading')}</h2>
          <p>{t('description')}</p>
        </div>

        <div className='pricing__grid'>
          <motion.div className='pricing__card' {...cardMotion(0)}>
            <div>
              <div className='pricing__plan-name'>{t('free.name')}</div>
              <p className='pricing__plan-desc'>{t('free.description')}</p>
            </div>
            <div className='pricing__price'>
              <span className='pricing__amount'>{currency === '€' ? '0€' : `${currency}0`}</span>
              <span className='pricing__period'>{t('free.period')}</span>
            </div>
            <div className='pricing__divider' />
            {perkList(freePerks)}
            <Link href='/login/signup' className='pricing__cta pricing__cta--outline'>
              {t('free.cta')}
            </Link>
          </motion.div>

          <motion.div className='pricing__card' {...cardMotion(0.1)}>
            <div>
              <div className='pricing__plan-name'>{t('menuPlan.name')}</div>
              <p className='pricing__plan-desc'>{t('menuPlan.description')}</p>
            </div>
            <div className='pricing__price'>
              <span className='pricing__amount'>{formatPlanPrice('menu', currency)}</span>
              <span className='pricing__period'>{t('menuPlan.period')}</span>
            </div>
            <div className='pricing__divider' />
            {perkList(menuPerks)}
            <Link href='/login/signup' className='pricing__cta'>
              {t('menuPlan.cta')} <ArrowRight size={16} />
            </Link>
          </motion.div>

          <motion.div className='pricing__card' {...cardMotion(0.2)}>
            <div>
              <div className='pricing__plan-name'>{t('fullPlan.name')}</div>
              <p className='pricing__plan-desc'>{t('fullPlan.description')}</p>
            </div>
            <div className='pricing__price'>
              <span className='pricing__amount'>{formatPlanPrice('full', currency)}</span>
              <span className='pricing__period'>{t('fullPlan.period')}</span>
            </div>
            <div className='pricing__divider' />
            {perkList(fullPerks)}
            <Link href='/login/signup' className='pricing__cta'>
              {t('fullPlan.cta')} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>

        <p className='pricing__footnote'>
          {t('footnote.prefix')} <Link href='/pricing'>{t('footnote.link')}</Link>
        </p>
      </div>
    </section>
  )
}

export default PricingV2
